import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/utils/api'

export interface Notification {
  id: string
  type: 'chore' | 'reward' | 'system' | 'calendar' | 'meal' | 'school'
  title: string
  message: string
  priority: 'low' | 'medium' | 'high'
  read: boolean
  created_at: string
  action_url?: string
  action_label?: string
  metadata?: any
}

// Query keys factory
export const notificationsKeys = {
  all: ['notifications'] as const,
  list: () => [...notificationsKeys.all, 'list'] as const,
}

/**
 * Fetch notifications
 */
export function useNotifications() {
  return useQuery({
    queryKey: notificationsKeys.list(),
    queryFn: async () => {
      try {
        const response = await apiClient.get('/api/notifications')
        return { notifications: response || [], isMock: false }
      } catch (error) {
        // Return mock data if API fails
        return {
          notifications: [
            {
              id: '1',
              type: 'chore' as const,
              title: 'Chore Reminder',
              message: 'You have 3 chores due today',
              priority: 'medium' as const,
              read: false,
              created_at: new Date().toISOString(),
              action_url: '/dashboard?view=chores',
              action_label: 'View Chores'
            },
            {
              id: '2',
              type: 'school' as const,
              title: 'Early Release Tomorrow',
              message: 'Kell High School has early release at 1:30 PM tomorrow',
              priority: 'high' as const,
              read: false,
              created_at: new Date(Date.now() - 3600000).toISOString()
            },
            {
              id: '3',
              type: 'meal' as const,
              title: 'Meal Plan Reminder',
              message: "Don't forget to plan next week's meals",
              priority: 'low' as const,
              read: true,
              created_at: new Date(Date.now() - 86400000).toISOString()
            }
          ] as Notification[],
          isMock: true
        }
      }
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 30 * 1000, // Poll every 30 seconds
    refetchOnWindowFocus: true,
  })
}

/**
 * Mark notification as read
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (notificationId: string) => {
      // TODO: Add real API call when available
      // await apiClient.patch(`/api/notifications/${notificationId}`, { read: true })
      return { id: notificationId }
    },
    onMutate: async (notificationId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: notificationsKeys.list() })

      // Snapshot previous value
      const previous = queryClient.getQueryData(notificationsKeys.list())

      // Optimistically update
      queryClient.setQueryData(notificationsKeys.list(), (old: any) => {
        if (!old) return old
        return {
          ...old,
          notifications: old.notifications.map((n: Notification) =>
            n.id === notificationId ? { ...n, read: true } : n
          )
        }
      })

      return { previous }
    },
    onError: (err, notificationId, context) => {
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueryData(notificationsKeys.list(), context.previous)
      }
    },
  })
}

/**
 * Mark all notifications as read
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      // TODO: Add real API call when available
      // await apiClient.post('/api/notifications/mark-all-read')
      return {}
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: notificationsKeys.list() })
      const previous = queryClient.getQueryData(notificationsKeys.list())

      queryClient.setQueryData(notificationsKeys.list(), (old: any) => {
        if (!old) return old
        return {
          ...old,
          notifications: old.notifications.map((n: Notification) => ({ ...n, read: true }))
        }
      })

      return { previous }
    },
    onError: (err, _, context) => {
      if (context?.previous) {
        queryClient.setQueryData(notificationsKeys.list(), context.previous)
      }
    },
  })
}

/**
 * Delete notification
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (notificationId: string) => {
      // TODO: Add real API call when available
      // await apiClient.delete(`/api/notifications/${notificationId}`)
      return { id: notificationId }
    },
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: notificationsKeys.list() })
      const previous = queryClient.getQueryData(notificationsKeys.list())

      queryClient.setQueryData(notificationsKeys.list(), (old: any) => {
        if (!old) return old
        return {
          ...old,
          notifications: old.notifications.filter((n: Notification) => n.id !== notificationId)
        }
      })

      return { previous }
    },
    onError: (err, _, context) => {
      if (context?.previous) {
        queryClient.setQueryData(notificationsKeys.list(), context.previous)
      }
    },
  })
}
